import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { TagList } from '@/components/admin/corkBoard/TagList';
import { TagFormModal } from '@/components/admin/corkBoard/TagFormModal';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import type { useTagManagement } from '@/hooks/admin/useTagManagement';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface DesktopTagManagementViewProps {
  tagManagement: ReturnType<typeof useTagManagement>;
  formVisible: boolean;
  editingTag: CorkBoardTag | null;
  onOpenCreate: () => void;
  onOpenEdit: (tag: CorkBoardTag) => void;
  onCloseForm: () => void;
  onSave: (data: { name: string; color: string | null; icon: string | null }) => void;
  onDelete: (tag: CorkBoardTag) => void;
}

export const DesktopTagManagementView: React.FC<DesktopTagManagementViewProps> = ({
  tagManagement,
  formVisible,
  editingTag,
  onOpenCreate,
  onOpenEdit,
  onCloseForm,
  onSave,
  onDelete,
}) => {
  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Cork Board Tags"
        subtitle="Manage the tags residents use to categorize their posts."
        showAddButton
        onAddPress={onOpenCreate}
        addButtonText="Add Tag"
      />

      {tagManagement.error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorBannerText}>Failed to load tags.</Text>
          <TouchableOpacity onPress={tagManagement.refetch}>
            <Text style={styles.errorRetryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.content}>
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Text style={styles.cardTitle}>Tags</Text>
            <Text style={styles.cardSubtitle}>
              {tagManagement.tags.length} tag{tagManagement.tags.length !== 1 ? 's' : ''}
            </Text>
          </View>

          <TagList
            tags={tagManagement.tags}
            isLoading={tagManagement.isLoading}
            onToggleEnabled={tagManagement.handleToggleEnabled}
            onEdit={onOpenEdit}
            onDelete={onDelete}
            onMoveUp={tagManagement.handleMoveUp}
            onMoveDown={tagManagement.handleMoveDown}
            onRefresh={tagManagement.refetch}
            isRefreshing={tagManagement.isFetching}
          />
        </View>
      </View>

      <TagFormModal
        visible={formVisible}
        tag={editingTag}
        isSaving={tagManagement.isCreating || tagManagement.isUpdating}
        onSave={onSave}
        onClose={onCloseForm}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: '#FEF2F2',
    borderBottomWidth: 1,
    borderBottomColor: '#FECACA',
  },
  errorBannerText: {
    fontSize: 13,
    color: ADMIN_COLORS.ERROR,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    textDecorationLine: 'underline',
  },
  content: {
    flex: 1,
  },
  card: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    overflow: 'hidden',
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  cardSubtitle: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
});
